import { useRef, useEffect } from 'react';
import { XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../tooltip';
import { VideoPlayer } from '../../video-player';
import { DropZone } from './drop-zone';

export function VideoUpload(props: {
	id?: string;
	currentUrl: string | undefined;
	file: File | undefined;
	onChange: (file: File | undefined) => void;
	onRemove: () => void;
	label: string;
	readOnly: boolean;
	emptyText?: string;
	noVideoText?: string;
	removeVideoText?: string;
	videoHintText?: string;
	dropZoneDescription?: string;
	children?: React.ReactNode;
}) {
	// Compute preview URL synchronously to avoid flash when replacing videos
	const prevFileRef = useRef<File | undefined>(undefined);
	const previewUrlRef = useRef<string | null>(null);
	if (props.file !== prevFileRef.current) {
		if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
		previewUrlRef.current = props.file ? URL.createObjectURL(props.file) : null;
		prevFileRef.current = props.file;
	}
	const preview = previewUrlRef.current;
	useEffect(
		() => () => {
			if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
		},
		[],
	);

	const displayUrl = preview ?? props.currentUrl;

	const handleFiles = (files: File[]) => {
		const f = files[0];
		if (!f?.type.startsWith('video/')) return;
		props.onChange(f);
	};

	const handleRemove = () => {
		props.onChange(undefined);
		props.onRemove();
	};

	return (
		<div className="flex flex-col">
			{props.readOnly && !displayUrl && (
				<p className="text-muted-foreground text-sm">{props.emptyText ?? props.noVideoText ?? 'No video uploaded'}</p>
			)}
			<AnimatePresence initial={false}>
				{displayUrl && (
					<motion.div
						key="preview"
						initial={{ height: 0 }}
						animate={{ height: 'auto' }}
						exit={{ height: 0 }}
						transition={{ type: 'spring', visualDuration: 0.2, bounce: 0 }}
						className="overflow-hidden"
					>
						<div className="pb-3">
							<div className="relative">
								<VideoPlayer src={displayUrl} className="rounded-xl" />
								{!props.readOnly && (
									<TooltipProvider>
										<div className="absolute top-1 right-1 z-20">
											<Tooltip>
												<TooltipTrigger asChild>
													<button
														type="button"
														aria-label={props.removeVideoText ?? 'Remove video'}
														onClick={handleRemove}
														className="rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-black/70"
													>
														<XIcon className="size-3.5" />
													</button>
												</TooltipTrigger>
												<TooltipContent>{props.removeVideoText ?? 'Remove video'}</TooltipContent>
											</Tooltip>
										</div>
									</TooltipProvider>
								)}
							</div>
						</div>
						{props.children && <div className="pb-3">{props.children}</div>}
					</motion.div>
				)}
			</AnimatePresence>
			<div
				className="grid"
				style={{
					gridTemplateRows: props.readOnly ? '0fr' : '1fr',
					opacity: props.readOnly ? 0 : 1,
					transition: 'grid-template-rows 200ms, opacity 200ms',
				}}
			>
				<div className="-m-1 overflow-hidden p-1">
					<DropZone
						id={props.id}
						label={props.label}
						accept="video/*"
						hint={props.videoHintText ?? 'MP4, WebM or MOV'}
						description={props.dropZoneDescription}
						onFiles={handleFiles}
					/>
				</div>
			</div>
		</div>
	);
}
